/**
 * Модель экрана «Мои поля»: строки таблицы, фильтр «В работе / Все / Без
 * культуры», порядок «сначала рабочие поля» и цепочка культур года.
 *
 * Чистые функции без React: правила экрана проверяются тестом, а не глазами.
 */

import { CROP_ORDER } from '../../../../lib/crops';
import type { ContourMeta, GeometryStatus } from '../../../data/types';

/**
 * Состояние поля в сезоне:
 *  - `work` — у поля есть работы плана идущего агроцикла (обязательства): так
 *    поле «в работе» считает и «Сегодня»;
 *  - `crop` — культура сезона названа, но работ идущего цикла у поля нет
 *    (например, «Прочие культуры» с работами закрытого цикла): поле только в
 *    «Все»;
 *  - `none` — ни работ, ни культуры сезона: пробел в данных, а не пар.
 */
export type FieldStatus = 'work' | 'crop' | 'none';

/** Что показывает таблица: «В работе» — поля с работами идущих агроциклов. */
export type FieldFilter = 'work' | 'all' | 'nocrop';

export const FIELD_FILTERS: readonly FieldFilter[] = ['work', 'all', 'nocrop'];

export interface SeasonCrops {
  season: string;
  /** Коды культур года по порядку: «Хлопок → Пшеница». */
  codes: string[];
}

export interface FieldRow {
  id: string;
  no: string;
  areaHa: number;
  areaStated: boolean;
  /** Культуры опорного сезона по порядку; пусто — культура не проставлена. */
  now: string[];
  /** Все сезоны поля, новые первыми: для карточки поля. */
  seasons: SeasonCrops[];
  status: FieldStatus;
  /** Привязка к карте: три исхода, а не два (см. `model/geometryLabel`). */
  geometry: GeometryStatus;
}

/**
 * Порядок культур внутри года. Озимая пшеница сеется осенью, после уборки
 * хлопка, поэтому в цепочке года она последняя; остальные идут в порядке
 * легенды карты.
 */
export function orderCrops(codes: readonly string[]): string[] {
  const rank = (code: string): number => {
    if (code === 'GRAIN') return 1000;
    const index = (CROP_ORDER as readonly string[]).indexOf(code);
    return index < 0 ? 999 : index;
  };
  return [...new Set(codes)].sort((a, b) => rank(a) - rank(b));
}

export interface PlacementBlockLike {
  season: string;
  rows: ReadonlyArray<{ contour_id: number | string; crop_code: string }>;
}

/**
 * Сезонные размещения хозяйства → культуры по контурам. Опорный сезон — самый
 * поздний из присланных. Один контур в сезоне может нести две культуры.
 */
export function placementsByContour(blocks: readonly PlacementBlockLike[]): {
  season: string | null;
  byContour: Map<string, SeasonCrops[]>;
} {
  const sorted = [...blocks].sort((a, b) => (a.season < b.season ? 1 : a.season > b.season ? -1 : 0));
  const byContour = new Map<string, SeasonCrops[]>();
  for (const block of sorted) {
    const perContour = new Map<string, string[]>();
    for (const row of block.rows) {
      const id = String(row.contour_id);
      const list = perContour.get(id);
      if (list) list.push(row.crop_code);
      else perContour.set(id, [row.crop_code]);
    }
    for (const [id, codes] of perContour) {
      const entry: SeasonCrops = { season: block.season, codes: orderCrops(codes) };
      const known = byContour.get(id);
      if (known) known.push(entry);
      else byContour.set(id, [entry]);
    }
  }
  return { season: sorted[0]?.season ?? null, byContour };
}

export function buildFieldRows(args: {
  contours: readonly ContourMeta[];
  /** Опорный сезон (год); null — размещений нет вовсе. */
  season: string | null;
  byContour: ReadonlyMap<string, SeasonCrops[]>;
  /** Контуры с работами идущих агроциклов (`fieldsInWork` из `farmer.ts`): по ним хозяйство работает сейчас. */
  activeContourIds: ReadonlySet<string>;
  geometryOf: (contour: ContourMeta) => GeometryStatus;
}): FieldRow[] {
  return args.contours.map((contour) => {
    const seasons = args.byContour.get(contour.id) ?? [];
    const fromPlacements = seasons.find((entry) => entry.season === args.season)?.codes ?? [];
    // Нет подробных размещений (фикстуры, не дочитали) — единственная культура
    // сезона из реестра контуров.
    const now = fromPlacements.length > 0 ? fromPlacements : contour.crop_2026 ? [contour.crop_2026] : [];
    const status: FieldStatus = args.activeContourIds.has(contour.id) ? 'work' : now.length === 0 ? 'none' : 'crop';
    return {
      id: contour.id,
      no: contour.no,
      areaHa: contour.area_ha,
      areaStated: contour.area_stated !== false,
      now,
      seasons,
      status,
      geometry: args.geometryOf(contour),
    };
  });
}

const RANK: Record<FieldStatus, number> = { work: 0, crop: 1, none: 2 };

/** Сначала поля с работами, затем с культурой, затем без; внутри — по номеру. */
export function sortFields(rows: readonly FieldRow[]): FieldRow[] {
  return [...rows].sort(
    (a, b) =>
      RANK[a.status] - RANK[b.status] ||
      a.no.localeCompare(b.no, 'ru', { numeric: true }) ||
      a.id.localeCompare(b.id, 'ru', { numeric: true }),
  );
}

/** Номер без «№» и пробелов: «№ 12» и «12» ищут одно и то же. */
export function normalizeQuery(query: string): string {
  return query.replace(/[№#\s]/g, '').toLowerCase();
}

export function inFilter(row: FieldRow, filter: FieldFilter): boolean {
  if (filter === 'all') return true;
  return filter === 'work' ? row.status === 'work' : row.status === 'none';
}

export function filterFields(rows: readonly FieldRow[], filter: FieldFilter, query: string): FieldRow[] {
  const q = normalizeQuery(query);
  return rows.filter((row) => inFilter(row, filter) && (q === '' || row.no.toLowerCase().includes(q)));
}

export interface FieldTotals {
  count: number;
  areaHa: number;
}

export interface FieldCounts {
  work: FieldTotals;
  all: FieldTotals;
  nocrop: FieldTotals;
}

/** Площадь — только названная: поле без площади не прибавляет нуля. */
export function countFields(rows: readonly FieldRow[]): FieldCounts {
  const out: FieldCounts = {
    work: { count: 0, areaHa: 0 },
    all: { count: 0, areaHa: 0 },
    nocrop: { count: 0, areaHa: 0 },
  };
  for (const row of rows) {
    const area = row.areaStated ? row.areaHa : 0;
    out.all.count += 1;
    out.all.areaHa += area;
    // «Без культуры» и «В работе» не пересекаются; поле с культурой, но без работ, — только в «Все».
    const bucket = row.status === 'none' ? out.nocrop : row.status === 'work' ? out.work : null;
    if (bucket) {
      bucket.count += 1;
      bucket.areaHa += area;
    }
  }
  return out;
}

/** Ключ цепочки для списка «Культуры на земле»: «COTTON>GRAIN». */
export function chainKey(codes: readonly string[]): string {
  return codes.join('>');
}

export interface ChainGroup {
  key: string;
  codes: string[];
  count: number;
  areaHa: number;
}

/**
 * Поля по цепочкам культур сезона. Поле с двумя культурами — одна группа
 * «Хлопок → Пшеница», а не два вхождения: гектары не удваиваются.
 */
export function groupByChain(rows: readonly FieldRow[]): ChainGroup[] {
  const map = new Map<string, ChainGroup>();
  for (const row of rows) {
    if (row.now.length === 0) continue;
    const key = chainKey(row.now);
    const hit = map.get(key) ?? { key, codes: row.now, count: 0, areaHa: 0 };
    hit.count += 1;
    hit.areaHa += row.areaStated ? row.areaHa : 0;
    map.set(key, hit);
  }
  return [...map.values()].sort((a, b) => b.areaHa - a.areaHa || b.count - a.count);
}
